/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// src/components/EmployeeCentral.js
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Button,
  Table,
  Avatar,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  IconButton,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  Divider,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { useTheme, alpha } from '@mui/material/styles';
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import ArchiveIcon from "@mui/icons-material/Archive";
import Groups2RoundedIcon from "@mui/icons-material/Groups2Rounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  orderBy,
  where,
  doc,
  getDoc,
  getDocs,
  deleteDoc,
  setDoc,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { useLocations } from "../hooks/useFirestoreData";
import TerminateEmployeeDialog from "./TerminateEmployeeDialog";
import { canAccessFeature } from "../utils/permissions";
import { tryResolveTenantId } from '../utils/tenant';

export default function EmployeeCentral() {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [quickFilter, setQuickFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'lastName', direction: 'asc' });
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [userPermission, setUserPermission] = useState(null);
  const [permissionError, setPermissionError] = useState('');
  const navigate = useNavigate();
  const theme = useTheme();

  // Load locations data
  const [selectedLocation, setSelectedLocation] = useState("");
  // NOTE: `useLocations` will auto-select the first location if `selectedLocation` is empty.
  // For the Employee Central filter, we want to default to "All locations" (empty string),
  // so we pass a no-op setter to prevent the hook from mutating this filter state on mount.
  const locations = useLocations(selectedLocation, () => {});

  const locationOptions = useMemo(() => {
    const opts = Array.isArray(locations) ? locations : [];
    return opts
      .filter((l) => l && !l.archived)
      .slice()
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [locations]);

  // Load user permissions
  useEffect(() => {
    const adminUser = localStorage.getItem('adminUser');
    if (adminUser) {
      try {
        const user = JSON.parse(adminUser);
        setUserPermission(user.permissionLevel);
      } catch (err) {
        console.error('Error parsing user data:', err);
      }
    }
  }, []);

  useEffect(() => {
    let unsub = null;
    let mounted = true;

  async function init() {
      try {
        setPermissionError('');
        let tenantId = null;
    let currentUserIsOwner = false;
        const adminRaw = localStorage.getItem('adminUser');
        if (adminRaw) {
          try {
      const admin = JSON.parse(adminRaw);
      tenantId = admin && admin.tenantId;
      if (admin && admin.permissionLevel === 'owner') currentUserIsOwner = true;
          } catch (e) {
            // ignore
          }
        }
        if (!tenantId && auth && auth.currentUser) {
          const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
          const utSnap = await getDoc(utRef);
          if (utSnap.exists()) {
            const ut = utSnap.data();
            tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
          }
        }
        if (!tenantId) {
          console.warn('EmployeeCentral: tenantId not found; cannot subscribe to employees.');
          if (mounted) setEmployees([]);
          setPermissionError('Tenant not detected. Please re-login.');
          return;
        }

        const q = query(collection(db, `tenants/${tenantId}/employees`), orderBy('lastName'));
        unsub = onSnapshot(q, (snap) => {
          const allEmployees = snap.docs.map((d) => {
            const data = d.data();
            // normalize permissionLevel to lowercase for consistency
            if (data && data.permissionLevel) data.permissionLevel = data.permissionLevel.toString().toLowerCase();
            return ({ id: d.id, ...data });
          });
          const activeEmployees = allEmployees.filter(emp => {
            const status = (emp.employment?.currentStatus || '').toString().toLowerCase();
            // If current user is owner, include owner accounts in Employee Central; otherwise hide owner accounts
            if (!currentUserIsOwner && (emp.permissionLevel || 'employee') === 'owner') return false;
            return !status || status !== 'terminated';
          });
          if (mounted) setEmployees(activeEmployees);
        }, (err) => {
          console.error('[firestore] onSnapshot error (EmployeeCentral employees):', err);
          if (mounted) {
            setEmployees([]);
            if (err && err.code === 'permission-denied') setPermissionError('Access denied to employees for this tenant.');
          }
        });
      } catch (err) {
        console.error('EmployeeCentral init error:', err);
        if (mounted) {
          setEmployees([]);
          setPermissionError('Unable to load employees.');
        }
      }
    }

    init();

    return () => {
      mounted = false;
      if (unsub) unsub();
    };
  }, []);

  // Load all departments and roles
  useEffect(() => {
    let unsubDept = null;
    let unsubRole = null;
    let mounted = true;

    (async () => {
      // Resolve tenant
      let tenantId = null;
      const adminRaw = localStorage.getItem('adminUser');
      if (adminRaw) {
        try { const admin = JSON.parse(adminRaw); tenantId = admin && admin.tenantId; } catch (e) { /* ignore */ }
      }
      if (!tenantId && auth && auth.currentUser) {
        try {
          const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
          const utSnap = await getDoc(utRef);
          if (utSnap.exists()) {
            const ut = utSnap.data();
            tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
          }
        } catch (e) {
          console.warn('Could not resolve tenant for departments/roles listeners', e);
          return;
        }
      }
      if (!tenantId) return;

      try {
        const deptQuery = query(collection(db, `tenants/${tenantId}/departments`), orderBy('name'));
        const roleQuery = query(collection(db, `tenants/${tenantId}/roles`), orderBy('name'));

  unsubDept = onSnapshot(deptQuery, snap => { if (!mounted) return; setDepartments(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(dd => !dd.archived)); }, err => { console.warn('departments listener error', err); setDepartments([]); });
  unsubRole = onSnapshot(roleQuery, snap => { if (!mounted) return; setRoles(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(r => !r.archived)); }, err => { console.warn('roles listener error', err); setRoles([]); });
      } catch (e) {
        console.warn('Failed to attach departments/roles listeners', e);
      }
    })();

    return () => { mounted = false; try { if (typeof unsubDept === 'function') unsubDept(); } catch (e) {} try { if (typeof unsubRole === 'function') unsubRole(); } catch (e) {} };
  }, []);

  const handleAddEmployee = async () => {
    if (!canAccessFeature(userPermission, 'employee_central_manage')) {
      setPermissionError('You do not have permission to add employees.');
      return;
    }
    // Resolve tenantId (same strategy as other components)
    let tenantId = null;
    const adminRaw = localStorage.getItem('adminUser');
    if (adminRaw) {
      try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
    }
    if (!tenantId && auth && auth.currentUser) {
      const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
      const utSnap = await getDoc(utRef);
      if (utSnap.exists()) {
        const ut = utSnap.data();
        tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
      }
    }
    if (!tenantId) {
      setPermissionError('Tenant not found. Please re-login.');
      return;
    }

    // Create a tenant employee document with a generated ID so admins can add employees
    // without creating or inviting an Auth user yet. Invitation can happen later.
    try {
      const employeeData = {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        pronouns: "",
        emergencyContactName: "",
        emergencyContactRelationship: "",
        emergencyContactPhone: "",
        assignments: {},
        rates: {},
        name: "",
        createdAt: new Date(),
        invited: false
      };

      const docRef = await addDoc(collection(db, `tenants/${tenantId}/employees`), employeeData);
      navigate(`/employees/${docRef.id}`);
    } catch (err) {
      console.error('Failed to create employee document:', err);
      setPermissionError('Failed to create employee.');
    }
  };

  const handleDelete = (employee) => {
    if (!canAccessFeature(userPermission, 'employee_termination')) {
      setPermissionError('You do not have permission to terminate employees.');
      return;
    }
    setSelectedEmployee(employee);
    setTerminateDialogOpen(true);
  };

  const handleTerminated = () => {
    // Employee list will automatically update due to the query filter
    setTerminateDialogOpen(false);
    setSelectedEmployee(null);
  };

  // Cache for reconstructed assignments for employees that don't have nested employee.assignments
  const [assignmentsCache, setAssignmentsCache] = useState({});

  // When the employees list updates, fetch flattened assignments for those who don't have nested assignments
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!employees || employees.length === 0) return;
        const tenantId = await tryResolveTenantId();
        if (!tenantId) return;

        const missing = employees.filter(e => !e.assignments || Object.keys(e.assignments).length === 0);
        if (!missing || missing.length === 0) return;

        const newCache = {};
        await Promise.all(missing.map(async (emp) => {
          try {
            const snap = await getDocs(collection(db, `tenants/${tenantId}/employees`, emp.id, 'assignments'));
            const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            const nestedMap = {};
            docs.forEach(d => {
              const locId = d.locationId;
              const deptId = d.departmentId || 'unknown_department';
              const roleId = d.roleId;
              if (!locId || !roleId) return;
              if (!nestedMap[locId]) nestedMap[locId] = { departments: {} };
              if (!nestedMap[locId].departments[deptId]) nestedMap[locId].departments[deptId] = { roles: {} };
              nestedMap[locId].departments[deptId].roles[roleId] = {
                assigned: typeof d.assigned === 'boolean' ? d.assigned : true,
                isPrimary: !!d.isPrimary,
                appearsOnSchedule: typeof d.appearsOnSchedule === 'boolean' ? d.appearsOnSchedule : true,
                wage: typeof d.wage !== 'undefined' ? d.wage : null
              };
            });
            if (Object.keys(nestedMap).length) newCache[emp.id] = nestedMap;
          } catch (err) {
            // ignore per-employee failures
            console.warn('Failed to fetch assignments for employee', emp.id, err);
          }
        }));
        if (!mounted) return;
        if (Object.keys(newCache).length) setAssignmentsCache(prev => ({ ...prev, ...newCache }));
      } catch (err) {
        console.warn('assignments cache population error', err);
      }
    })();

    return () => { mounted = false; };
  }, [employees]);

  // Helper: Get employee's assigned locations from assignments
  const getAssignmentsForEmployee = (employee) => {
    if (!employee) return {};
    return (employee && employee.assignments && Object.keys(employee.assignments).length) ? employee.assignments : (assignmentsCache[employee.id] || {});
  };

  // Helper: Get employee's assigned locations from assignments
  const getEmployeeLocations = (employee) => {
    const assignments = getAssignmentsForEmployee(employee);
    if (!assignments || typeof assignments !== "object") return [];

    const locationIds = Object.keys(assignments);
    if (locationIds.length === 0) return [];

    return locationIds.map(locationId => {
      const location = locations.find(loc => loc.id === locationId);
      return location?.name || "Unknown Location";
    }).filter(name => name !== "Unknown Location");
  };

  // Helper: Get employee's primary location (for sorting/searching)
  const getEmployeePrimaryLocation = (employee) => {
    const locations = getEmployeeLocations(employee);
    return locations.length > 0 ? locations.join(", ") : "No Location";
  };

  // Helper: deduped role display names for an employee (across all locations/departments)
  const getEmployeeRoleNames = (employee) => {
    const assignments = getAssignmentsForEmployee(employee);
    if (!assignments || typeof assignments !== 'object') return [];

    const roleIds = new Set();
    Object.keys(assignments).forEach((locationId) => {
      const locAssignment = assignments[locationId];
      const departmentsMap = locAssignment?.departments;
      if (!departmentsMap || typeof departmentsMap !== 'object') return;
      Object.keys(departmentsMap).forEach((deptId) => {
        const deptAssignment = departmentsMap[deptId];
        const rolesMap = deptAssignment?.roles;
        if (!rolesMap || typeof rolesMap !== 'object') return;
        Object.keys(rolesMap).forEach((roleId) => {
          const roleData = rolesMap[roleId];
          if (roleData?.assigned === false) return;
          roleIds.add(roleId);
        });
      });
    });

    const names = Array.from(roleIds)
      .map((id) => roles.find((r) => r.id === id)?.name)
      .filter(Boolean);

    // stable sort for display
    return names.sort((a, b) => a.localeCompare(b));
  };

  // Helper: Get employee's primary role
  const getEmployeePrimaryRole = (employee) => {
  const assignments = getAssignmentsForEmployee(employee);
    if (!assignments || typeof assignments !== 'object') return null;

    for (const locationId in assignments) {
      const locationAssignment = assignments[locationId];
      if (locationAssignment?.departments) {
        for (const deptId in locationAssignment.departments) {
          const deptAssignment = locationAssignment.departments[deptId];
          if (deptAssignment?.roles && typeof deptAssignment.roles === 'object') {
            for (const roleId in deptAssignment.roles) {
              const roleData = deptAssignment.roles[roleId];
              if (roleData?.isPrimary && roleData?.assigned) {
                const role = roles.find(r => r.id === roleId);
                return role || null;
              }
            }
          }
        }
      }
    }

    for (const locationId in assignments) {
      const locationAssignment = assignments[locationId];
      if (locationAssignment?.departments) {
        for (const deptId in locationAssignment.departments) {
          const deptAssignment = locationAssignment.departments[deptId];
          if (deptAssignment?.roles) {
            if (Array.isArray(deptAssignment.roles)) {
              const firstRoleId = deptAssignment.roles[0];
              if (firstRoleId) {
                const role = roles.find(r => r.id === firstRoleId);
                return role || null;
              }
            } else {
              for (const roleId in deptAssignment.roles) {
                const roleData = deptAssignment.roles[roleId];
                if (roleData?.assigned) {
                  const role = roles.find(r => r.id === roleId);
                  return role || null;
                }
              }
            }
          }
        }
      }
    }

    return null;
  };

  // Helper: Get employee's primary department
  // Prefer the department that contains a primary role; otherwise return the first assigned department
  const getEmployeePrimaryDepartment = (employee) => {
    const assignments = getAssignmentsForEmployee(employee);
    if (!assignments || typeof assignments !== 'object') return null;

    for (const locationId in assignments) {
      const locationAssignment = assignments[locationId];
      if (locationAssignment?.departments) {
        for (const deptId in locationAssignment.departments) {
          const deptAssignment = locationAssignment.departments[deptId];
          if (deptAssignment?.roles && typeof deptAssignment.roles === 'object') {
            for (const roleId in deptAssignment.roles) {
              const roleData = deptAssignment.roles[roleId];
              if (roleData?.isPrimary && roleData?.assigned) {
                const dept = departments.find(d => d.id === deptId);
                return dept || { id: deptId, name: 'Unknown Department' };
              }
            }
          }
        }
      }
    }

    for (const locationId in assignments) {
      const locationAssignment = assignments[locationId];
      if (locationAssignment?.departments) {
        for (const deptId in locationAssignment.departments) {
          const dept = departments.find(d => d.id === deptId);
          if (dept) return dept;
        }
      }
    }

    return null;
  };

  const hasRoleAssignmentWithWage = (employee) => {
    const assignments = getAssignmentsForEmployee(employee);
    if (!assignments || typeof assignments !== 'object') return false;

    const employeeRates = employee?.rates && typeof employee.rates === 'object' ? employee.rates : {};
    const asNumberOrNull = (value) => {
      if (value === null || value === undefined || value === '') return null;
      const num = typeof value === 'number' ? value : Number(value);
      return Number.isFinite(num) ? num : null;
    };

    for (const locationId of Object.keys(assignments)) {
      const locationAssignment = assignments[locationId];
      const departmentsMap = locationAssignment?.departments;
      if (!departmentsMap || typeof departmentsMap !== 'object') continue;

      for (const deptId of Object.keys(departmentsMap)) {
        const deptAssignment = departmentsMap[deptId];
        const rolesMap = deptAssignment?.roles;
        if (!rolesMap || typeof rolesMap !== 'object' || Array.isArray(rolesMap)) continue;

        for (const roleId of Object.keys(rolesMap)) {
          const roleData = rolesMap[roleId];
          if (!roleData?.assigned) continue;

          const assignmentWage = asNumberOrNull(roleData.wage);
          const employeeRate = asNumberOrNull(employeeRates[roleId]);
          if (assignmentWage !== null || employeeRate !== null) {
            return true;
          }
        }
      }
    }

    return false;
  };

  const isEmployeeSetupComplete = (employee) => {
    return (
      getEmployeeLocations(employee).length > 0
      && getEmployeeRoleNames(employee).length > 0
      && hasRoleAssignmentWithWage(employee)
    );
  };

  const isEmployeeNeedsSetup = (employee) => !isEmployeeSetupComplete(employee);

  // Helper: Get employee's full name
  const getEmployeeFullName = (employee) => {
    if (employee.firstName && employee.lastName) {
      return `${employee.firstName} ${employee.lastName}`;
    }
    return employee.name || "Unknown Employee";
  };

  // Helper: initials for Avatar fallback
  const getEmployeeInitials = (employee) => {
    const full = getEmployeeFullName(employee).trim();
    if (!full || full === 'Unknown Employee') return '';
    const parts = full.split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0,2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Sorting functionality
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filter and sort employees
  const filteredAndSortedEmployees = useMemo(() => {
    const isLeader = (employee) => {
      const level = (employee.permissionLevel || 'employee').toString().toLowerCase();
      return ['owner', 'manager', 'schedule_manager'].includes(level);
    };

    // First: apply explicit location filter (dropdown)
    const locationFiltered = !selectedLocation
      ? employees
      : employees.filter((employee) => {
          const assignments = getAssignmentsForEmployee(employee);
          if (!assignments || typeof assignments !== 'object') return false;
          return Object.prototype.hasOwnProperty.call(assignments, selectedLocation);
        });

    const quickFiltered = quickFilter === 'needs_setup'
      ? locationFiltered.filter(isEmployeeNeedsSetup)
      : quickFilter === 'leaders'
        ? locationFiltered.filter(isLeader)
      : locationFiltered;

    let filtered = quickFiltered.filter(employee => {
      const fullName = getEmployeeFullName(employee).toLowerCase();
      const location = getEmployeePrimaryLocation(employee).toLowerCase();
      const role = getEmployeePrimaryRole(employee);
      const roleName = role ? role.name.toLowerCase() : '';
      
      const searchLower = searchTerm.toLowerCase();
      
      return fullName.includes(searchLower) || 
             location.includes(searchLower) || 
             roleName.includes(searchLower) ||
             (employee.firstName && employee.firstName.toLowerCase().includes(searchLower)) ||
             (employee.lastName && employee.lastName.toLowerCase().includes(searchLower));
    });

    // Sort the filtered results
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'lastName':
          aValue = a.lastName || a.name || '';
          bValue = b.lastName || b.name || '';
          break;
        case 'firstName':
          aValue = a.firstName || '';
          bValue = b.firstName || '';
          break;
        case 'location':
          aValue = getEmployeePrimaryLocation(a);
          bValue = getEmployeePrimaryLocation(b);
          break;
        case 'role':
          const roleA = getEmployeePrimaryRole(a);
          const roleB = getEmployeePrimaryRole(b);
          aValue = roleA ? roleA.name : '';
          bValue = roleB ? roleB.name : '';
          break;
        case 'permissions':
          aValue = a.permissionLevel || 'employee';
          bValue = b.permissionLevel || 'employee';
          break;
        default:
          aValue = '';
          bValue = '';
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [employees, selectedLocation, searchTerm, sortConfig, locations, roles, assignmentsCache, quickFilter]);

  const canManageEmployees = canAccessFeature(userPermission, 'employee_central_manage');
  const canTerminateEmployees = canAccessFeature(userPermission, 'employee_termination');
  const canViewPermissionColumn = canAccessFeature(userPermission, 'employee_central_manage') || canAccessFeature(userPermission, 'user_permissions');
  const showActionsColumn = canTerminateEmployees;
  const emptyStateColSpan = 5 + (canViewPermissionColumn ? 1 : 0) + (showActionsColumn ? 1 : 0);

  const selectionSummary = useMemo(() => {
    const total = employees?.length || 0;
    const shown = filteredAndSortedEmployees?.length || 0;
    return { total, shown };
  }, [employees, filteredAndSortedEmployees]);

  const hasActiveFilters = Boolean(searchTerm || selectedLocation);
  const selectedLocationLabel = selectedLocation
    ? (locationOptions.find((loc) => loc.id === selectedLocation)?.name || 'Selected location')
    : '';

  const pageStats = useMemo(() => {
    const total = employees.length;
    const needsSetup = employees.filter(isEmployeeNeedsSetup).length;
    const leaders = employees.filter((employee) => {
      const level = (employee.permissionLevel || 'employee').toString().toLowerCase();
      return ['owner', 'manager', 'schedule_manager'].includes(level);
    }).length;

    return [
      {
        id: 'active_team',
        label: 'Active Team',
        value: total,
        helper: `${selectionSummary.shown} currently in view`,
        icon: <Groups2RoundedIcon fontSize="small" />,
      },
      {
        id: 'needs_setup',
        label: 'Needs Setup',
        value: needsSetup,
        helper: 'Missing a role, location, or wage',
        icon: <TuneRoundedIcon fontSize="small" />,
      },
      {
        id: 'leaders',
        label: 'Leaders',
        value: leaders,
        helper: 'Owner, manager, or schedule manager',
        icon: <AdminPanelSettingsRoundedIcon fontSize="small" />,
      },
    ];
  }, [employees, selectionSummary.shown, locations, roles, assignmentsCache]);

  const getPermissionPresentation = (permissionLevel) => {
    const normalized = (permissionLevel || 'employee').toString().toLowerCase();

    switch (normalized) {
      case 'owner':
        return {
          label: 'Owner',
          sx: {
            color: '#b42318',
            bgcolor: alpha('#f04438', 0.1),
            borderColor: alpha('#f04438', 0.35),
          },
        };
      case 'manager':
        return {
          label: 'Manager',
          sx: {
            color: '#b54708',
            bgcolor: alpha('#f79009', 0.12),
            borderColor: alpha('#f79009', 0.35),
          },
        };
      case 'schedule_manager':
        return {
          label: 'Schedule Manager',
          sx: {
            color: '#175cd3',
            bgcolor: alpha('#2e90fa', 0.12),
            borderColor: alpha('#2e90fa', 0.35),
          },
        };
      default:
        return {
          label: 'Employee',
          sx: {
            color: '#067647',
            bgcolor: alpha('#12b76a', 0.1),
            borderColor: alpha('#12b76a', 0.35),
          },
        };
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 0, mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 4,
          overflow: 'hidden',
          background: theme.palette.mode === 'dark'
            ? `linear-gradient(180deg, ${alpha(theme.palette.primary.dark, 0.18)} 0%, ${theme.palette.background.paper} 42%)`
            : `linear-gradient(180deg, ${alpha('#1d4ed8', 0.08)} 0%, ${alpha('#0f766e', 0.06)} 38%, ${theme.palette.background.paper} 70%)`,
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.08)',
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            pt: { xs: 2.5, md: 3 },
            pb: { xs: 2.25, md: 3 },
            background: theme.palette.mode === 'dark'
              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.32)} 0%, ${alpha(theme.palette.secondary.main, 0.18)} 100%)`
              : 'linear-gradient(135deg, rgba(29,78,216,0.95) 0%, rgba(37,99,235,0.88) 48%, rgba(13,148,136,0.82) 100%)',
            color: 'common.white',
          }}
        >
          <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ justifyContent: 'space-between', alignItems: { lg: 'flex-start' } }}>
              <Box sx={{ maxWidth: 720 }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, letterSpacing: 1.3, textTransform: 'uppercase', opacity: 0.78 }}>
                  Team Directory
                </Typography>
                <Typography sx={{ mt: 0.75, fontSize: { xs: '2.2rem', md: '3.1rem' }, fontWeight: 900, letterSpacing: -1.1, lineHeight: 0.98 }}>
                  Employee Central
                </Typography>
                <Typography sx={{ mt: 1, maxWidth: 620, fontSize: { xs: '1rem', md: '1.08rem' }, lineHeight: 1.6, color: alpha(theme.palette.common.white, 0.82) }}>
                  Manage team members, roles, permissions, and locations from one clean workspace built for fast staffing decisions.
                </Typography>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', lg: 'auto' } }}>
                {canTerminateEmployees && (
                  <Button
                    variant="outlined"
                    startIcon={<ArchiveIcon />}
                    onClick={() => navigate('/employees/terminated')}
                    sx={{
                      minHeight: 46,
                      px: 2,
                      color: 'common.white',
                      borderColor: alpha(theme.palette.common.white, 0.3),
                      bgcolor: alpha(theme.palette.common.white, 0.08),
                      fontWeight: 800,
                      '&:hover': {
                        borderColor: alpha(theme.palette.common.white, 0.5),
                        bgcolor: alpha(theme.palette.common.white, 0.14),
                      },
                    }}
                  >
                    View Archive
                  </Button>
                )}

                {canManageEmployees && (
                  <Button
                    variant="contained"
                    onClick={handleAddEmployee}
                    sx={{
                      minHeight: 46,
                      px: 2.4,
                      fontWeight: 900,
                      color: theme.palette.primary.dark,
                      bgcolor: '#fff',
                      boxShadow: '0 10px 28px rgba(8, 15, 52, 0.2)',
                      '&:hover': {
                        bgcolor: alpha('#ffffff', 0.92),
                      },
                    }}
                  >
                    Add Employee
                  </Button>
                )}
              </Stack>
            </Stack>

            <Grid container spacing={1.25}>
              {pageStats.map((stat) => {
                const isNeedsSetupCard = stat.id === 'needs_setup';
                const isLeadersCard = stat.id === 'leaders';
                const isInteractive = isNeedsSetupCard || isLeadersCard;
                const isActive = (isNeedsSetupCard && quickFilter === 'needs_setup')
                  || (isLeadersCard && quickFilter === 'leaders');
                const activeCardTone = isNeedsSetupCard
                  ? {
                      bg: 'linear-gradient(135deg, rgba(245, 158, 11, 0.34) 0%, rgba(239, 68, 68, 0.28) 100%)',
                      border: alpha('#fde68a', 0.9),
                      shadow: '0 18px 34px rgba(146, 64, 14, 0.28)',
                    }
                  : {
                      bg: 'linear-gradient(135deg, rgba(124, 58, 237, 0.32) 0%, rgba(59, 130, 246, 0.24) 100%)',
                      border: alpha('#ddd6fe', 0.88),
                      shadow: '0 18px 34px rgba(49, 46, 129, 0.26)',
                    };

                return (
                <Grid item xs={12} sm={6} lg={3} key={stat.label}>
                  <Box
                    onClick={isInteractive ? () => {
                      setQuickFilter((prev) => {
                        if (isNeedsSetupCard) return prev === 'needs_setup' ? 'all' : 'needs_setup';
                        if (isLeadersCard) return prev === 'leaders' ? 'all' : 'leaders';
                        return prev;
                      });
                    } : undefined}
                    sx={{
                      height: '100%',
                      borderRadius: 3,
                      px: 1.75,
                      py: 1.5,
                      bgcolor: isActive ? undefined : alpha(theme.palette.common.white, 0.12),
                      background: isActive ? activeCardTone.bg : undefined,
                      border: '1px solid',
                      borderColor: isActive ? activeCardTone.border : alpha(theme.palette.common.white, 0.18),
                      backdropFilter: 'blur(8px)',
                      cursor: isInteractive ? 'pointer' : 'default',
                      transform: isActive ? 'translateY(-1px)' : 'none',
                      boxShadow: isActive ? activeCardTone.shadow : 'none',
                      transition: 'all 160ms ease',
                      '&:hover': isInteractive ? {
                        bgcolor: alpha(theme.palette.common.white, isActive ? 0.22 : 0.16),
                        borderColor: isActive ? activeCardTone.border : alpha(theme.palette.common.white, 0.34),
                      } : undefined,
                    }}
                  >
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 2.5,
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: alpha(theme.palette.common.white, 0.18),
                          color: 'common.white',
                        }}
                      >
                        {stat.icon}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.9, color: alpha(theme.palette.common.white, 0.72) }}>
                          {stat.label}
                        </Typography>
                        <Typography sx={{ mt: 0.25, fontSize: '1.6rem', fontWeight: 900, lineHeight: 1 }}>
                          {stat.value}
                        </Typography>
                      </Box>
                    </Stack>
                    <Typography sx={{ mt: 1.1, fontSize: '0.82rem', color: alpha(theme.palette.common.white, 0.78) }}>
                      {stat.helper}
                    </Typography>
                  </Box>
                </Grid>
                );
              })}
            </Grid>
          </Stack>
        </Box>

        <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 2.5 } }}>
          <Box
            sx={{
              p: { xs: 1.5, md: 2 },
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: alpha(theme.palette.background.paper, 0.96),
              boxShadow: '0 14px 30px rgba(15, 23, 42, 0.05)',
            }}
          >
            <Stack spacing={1.5}>
              <Stack direction={{ xs: 'column', xl: 'row' }} spacing={1.25} sx={{ alignItems: { xl: 'center' } }}>
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder="Search by name, location, or role..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: { xl: 1.2 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2.5,
                      bgcolor: alpha(theme.palette.background.default, 0.55),
                    },
                  }}
                />

                <FormControl size="small" sx={{ width: { xs: '100%', xl: 260 } }}>
                  <InputLabel id="employee-central-location-label">Location</InputLabel>
                  <Select
                    labelId="employee-central-location-label"
                    value={selectedLocation}
                    label="Location"
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    sx={{ borderRadius: 2.5, bgcolor: alpha(theme.palette.background.default, 0.55) }}
                  >
                    <MenuItem value=""><em>All locations</em></MenuItem>
                    {locationOptions.map((loc) => (
                      <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ flex: 1 }} />

                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                  <Chip
                    label={`${selectionSummary.shown} of ${selectionSummary.total} shown`}
                    sx={{
                      fontWeight: 800,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      color: 'primary.main',
                      border: '1px solid',
                      borderColor: alpha(theme.palette.primary.main, 0.18),
                    }}
                  />
                  {(hasActiveFilters || quickFilter !== 'all') && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedLocation('');
                        setQuickFilter('all');
                      }}
                      sx={{ fontWeight: 800 }}
                    >
                      Clear Filters
                    </Button>
                  )}
                </Stack>
              </Stack>

              {(searchTerm || selectedLocation || quickFilter !== 'all') && (
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                  {searchTerm && (
                    <Chip
                      label={`Search: ${searchTerm}`}
                      onDelete={() => setSearchTerm('')}
                      sx={{ fontWeight: 700 }}
                    />
                  )}
                  {selectedLocation && (
                    <Chip
                      label={`Location: ${selectedLocationLabel}`}
                      onDelete={() => setSelectedLocation('')}
                      sx={{ fontWeight: 700 }}
                    />
                  )}
                  {quickFilter === 'needs_setup' && (
                    <Chip
                      label="Needs Setup"
                      onDelete={() => setQuickFilter('all')}
                      color="primary"
                      sx={{ fontWeight: 800 }}
                    />
                  )}
                  {quickFilter === 'leaders' && (
                    <Chip
                      label="Leaders Only"
                      onDelete={() => setQuickFilter('all')}
                      color="secondary"
                      sx={{ fontWeight: 800 }}
                    />
                  )}
                </Stack>
              )}
            </Stack>
          </Box>
        </Box>

        <Box sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2.25, md: 3 } }}>
          <Box
            sx={{
              overflow: 'hidden',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              boxShadow: '0 18px 36px rgba(15, 23, 42, 0.05)',
            }}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Table stickyHeader sx={{ minWidth: 980 }}>
          <TableHead>
            <TableRow
              sx={(theme) => ({
                '& .MuiTableCell-root': {
                  fontWeight: 800,
                  fontSize: '0.76rem',
                  textTransform: 'uppercase',
                  letterSpacing: 0.7,
                  color: theme.palette.text.secondary,
                  bgcolor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.grey[900], 0.96)
                    : alpha(theme.palette.grey[50], 0.98),
                  borderBottom: '1px solid',
                  borderColor: theme.palette.divider,
                  py: 1.5,
                },
              })}
            >
              <TableCell>Employee</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'lastName'}
                  direction={sortConfig.key === 'lastName' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('lastName')}
                  sx={{ fontWeight: 800 }}
                >
                  Last Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'firstName'}
                  direction={sortConfig.key === 'firstName' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('firstName')}
                  sx={{ fontWeight: 800 }}
                >
                  First Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'location'}
                  direction={sortConfig.key === 'location' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('location')}
                  sx={{ fontWeight: 800 }}
                >
                  Location
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'role'}
                  direction={sortConfig.key === 'role' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('role')}
                  sx={{ fontWeight: 800 }}
                >
                  Role
                </TableSortLabel>
              </TableCell>
              {canViewPermissionColumn && (
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === 'permissions'}
                    direction={sortConfig.key === 'permissions' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('permissions')}
                    sx={{ fontWeight: 800 }}
                  >
                    Permissions
                  </TableSortLabel>
                </TableCell>
              )}
              {showActionsColumn && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedEmployees.map((emp) => {
              const employeeLocations = getEmployeeLocations(emp);
              const roleNames = getEmployeeRoleNames(emp);
              const permission = getPermissionPresentation(emp.permissionLevel);

              return (
                <TableRow
                  key={emp.id}
                  hover
                  onClick={() => navigate(`/employees/${emp.id}`)}
                  sx={{ 
                    cursor: "pointer",
                    '&:nth-of-type(odd)': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.01)' : 'rgba(15,23,42,0.012)' },
                    '& .MuiTableCell-root': {
                      py: 1.75,
                      borderBottomColor: alpha(theme.palette.divider, 0.85),
                    },
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.05),
                    },
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      {(() => {
                        const photo = emp.photoURL || emp.photoUrl || (emp.photo && emp.photo.url) || null;
                        if (photo) {
                          return <Avatar alt={getEmployeeFullName(emp)} src={photo} sx={{ width: 42, height: 42, boxShadow: '0 10px 20px rgba(15, 23, 42, 0.12)' }} />;
                        }
                        return (
                          <Avatar sx={{ width: 42, height: 42, bgcolor: theme.palette.primary.main, color: '#fff', fontWeight: 700, boxShadow: '0 10px 20px rgba(37, 99, 235, 0.2)' }}>
                            {getEmployeeInitials(emp)}
                          </Avatar>
                        );
                      })()}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          {getEmployeeFullName(emp)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15 }}>
                          {emp.email || 'No email on file'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {emp.lastName || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {emp.firstName || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {!employeeLocations || employeeLocations.length === 0 ? (
                      <Chip label="No Location" size="small" variant="outlined" sx={{ color: 'text.secondary', borderColor: alpha(theme.palette.text.secondary, 0.25) }} />
                    ) : (
                      <Tooltip title={employeeLocations.join(', ')}>
                        <Chip
                          label={employeeLocations.length === 1 ? employeeLocations[0] : `${employeeLocations.length} locations`}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            maxWidth: 180,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            color: 'primary.main',
                          }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    {!roleNames || roleNames.length === 0 ? (
                      <Chip label="No Role" size="small" variant="outlined" sx={{ color: 'text.secondary', borderColor: alpha(theme.palette.text.secondary, 0.25) }} />
                    ) : (
                      <Tooltip title={roleNames.join(', ')}>
                        <Chip
                          label={roleNames.length === 1 ? roleNames[0] : `${roleNames.length} roles`}
                          size="small"
                          sx={{
                            fontWeight: 800,
                            maxWidth: 180,
                            bgcolor: alpha('#7c3aed', 0.08),
                            color: '#6d28d9',
                          }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  {canViewPermissionColumn && (
                    <TableCell>
                      <Chip
                        label={permission.label}
                        size="small"
                        sx={{
                          fontWeight: 800,
                          fontSize: '0.76rem',
                          border: '1px solid',
                          ...permission.sx,
                        }}
                      />
                    </TableCell>
                  )}
                  {showActionsColumn && (
                    <TableCell align="right">
                      {canTerminateEmployees && (
                        <Tooltip title="Terminate Employee">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(emp);
                            }}
                            sx={{
                              bgcolor: alpha(theme.palette.warning.main, 0.1),
                              border: '1px solid',
                              borderColor: alpha(theme.palette.warning.main, 0.18),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.warning.main, 0.16),
                              },
                            }}
                          >
                            <PersonRemoveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {filteredAndSortedEmployees.length === 0 && (
              <TableRow>
                <TableCell colSpan={emptyStateColSpan} align="center" sx={{ py: 6 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 76,
                        height: 76,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: '50%',
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 38, color: 'primary.main' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {searchTerm ? 'No employees found matching your search' : 'No employees found'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try adjusting your search or location filters to broaden the results.
                    </Typography>
                    {searchTerm && (
                      <Button 
                        variant="outlined" 
                        onClick={() => setSearchTerm('')}
                        size="small"
                      >
                        Clear Search
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

            </Box>

            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: alpha(theme.palette.background.default, 0.55),
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Showing {selectionSummary.shown} of {selectionSummary.total} active employees
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click any row to open the full employee profile
              </Typography>
            </Box>
          </Box>
        </Box>

        <TerminateEmployeeDialog
          open={terminateDialogOpen}
          onClose={() => setTerminateDialogOpen(false)}
          employee={selectedEmployee}
          onTerminated={handleTerminated}
        />
        {permissionError && (
          <Box sx={{ px: { xs: 2, md: 3 }, pb: 2.5 }}>
            <Box
              sx={{
                px: 1.5,
                py: 1.2,
                borderRadius: 2.5,
                bgcolor: alpha(theme.palette.error.main, 0.08),
                border: '1px solid',
                borderColor: alpha(theme.palette.error.main, 0.18),
              }}
            >
              <Typography variant="body2" color="error.main" sx={{ fontWeight: 700 }}>
                {permissionError}
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
}
